import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { useState } from "react";
import { CheckCircle2, HardDrive, MoreVertical, Pencil, RotateCcw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { FORMAS_DE_PAGAMENTO, FORMA_DE_PAGAMENTO_LABELS, hojeEmTexto, type FormaDePagamento } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais, type LancamentoDaLista } from "@/lib/dinheiro";
import { campo, rotulo } from "../estilos";
import { BemDialog, bemVazio, type BemInicial } from "../config/BemDialog";
import { PreviaDaTaxa } from "../a-receber/PreviaDaTaxa";

const item = "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm";
const botaoPrimario = "h-9 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50";
const botaoPerigo = "h-9 rounded-md bg-red-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50";
const botaoSecundario = "h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted";
const flutuante = "border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card";

/** Menu ⋮ da linha: marcar pago, desfazer, editar e estornar (o que couber na situação). */
export function AcoesDoLancamento({ l, onEditar }: { l: LancamentoDaLista; onEditar: () => void }) {
  const utils = trpc.useUtils();
  const [dialogo, setDialogo] = useState<"pagar" | "estornar" | null>(null);
  const [bem, setBem] = useState<BemInicial | null>(null);
  // Compra de equipamento com o DRE ligado: vira bem para a depreciação (se ainda não virou).
  const dreLigado = !!trpc.financeiro.configFinanceira.ler.useQuery().data?.dreLigado;
  const ehCompra = dreLigado && l.tipo === "SAIDA" && l.categoria.nome === "Compra de equipamentos";
  const bens = trpc.financeiro.bens.listar.useQuery(undefined, { enabled: ehCompra }).data;
  const podeVirarBem = ehCompra && !!bens && !bens.some((b) => b.lancamentoId === l.id);
  const [pagoEm, setPagoEm] = useState(hojeEmTexto());
  const [forma, setForma] = useState<FormaDePagamento | "">((l.formaDePagamento as FormaDePagamento) ?? "");
  const [motivo, setMotivo] = useState("");
  const feito = (msg: string) => ({
    onSuccess: () => {
      toast.success(msg);
      utils.financeiro.invalidate();
      setDialogo(null);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });
  const pagar = trpc.financeiro.lancamentos.marcarPago.useMutation(feito(l.tipo === "SAIDA" ? "Marcado como pago." : "Marcado como recebido."));
  const desfazer = trpc.financeiro.lancamentos.desmarcarPago.useMutation(feito("Pagamento desfeito."));
  const estornar = trpc.financeiro.lancamentos.estornar.useMutation(feito("Lançamento estornado."));

  if (l.ehEstorno || l.situacao === "ESTORNADO") return null;
  const pago = l.situacao === "PAGO";
  // Perda não se paga nem se edita: desfaz-se estornando.
  const perda = l.origem === "PERDA";
  const editavel = !pago && (l.origem === "MANUAL" || l.origem === "RECORRENTE" || l.origem === "IMPORTACAO");
  const verbo = l.tipo === "SAIDA" ? "pago" : "recebido";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label={`Ações de ${l.descricao}`} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-muted" onClick={(e) => e.stopPropagation()}>
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={`min-w-[190px] p-1 ${flutuante}`}>
          {!pago && !perda && (
            <DropdownMenuItem className={item} onClick={() => setDialogo("pagar")}>
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Marcar como {verbo}
            </DropdownMenuItem>
          )}
          {pago && (
            <DropdownMenuItem className={item} onClick={() => desfazer.mutate({ id: l.id })}>
              <Undo2 className="h-4 w-4 text-zinc-500" /> Desfazer {l.tipo === "SAIDA" ? "pagamento" : "recebimento"}
            </DropdownMenuItem>
          )}
          {podeVirarBem && !l.ehEstorno && (
            <DropdownMenuItem className={item} onClick={() => setBem({ ...bemVazio(), nome: l.descricao.slice(0, 80), valorCents: l.valorCents, compradoEm: l.competencia, lancamentoId: l.id })}>
              <HardDrive className="h-4 w-4 text-zinc-500" /> Cadastrar como bem
            </DropdownMenuItem>
          )}
          {editavel && (
            <DropdownMenuItem className={item} onClick={onEditar}>
              <Pencil className="h-4 w-4 text-zinc-500" /> Editar
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className={`${item} text-red-600`} onClick={() => setDialogo("estornar")}>
            <RotateCcw className="h-4 w-4" /> Estornar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <BemDialog inicial={bem} onFechar={() => setBem(null)} />
      <Dialog open={dialogo === "pagar"} onOpenChange={(v) => !v && setDialogo(null)}>
        <DialogContent className={`max-w-sm ${flutuante}`}>
          <DialogHeader>
            <DialogTitle ajuda={AJUDA_FINANCEIRO.marcarPago}>Marcar como {verbo}</DialogTitle>
            <DialogDescription>
              {l.descricao} · {reais(l.valorCents)}
            </DialogDescription>
          </DialogHeader>
          <label>
            <span className={rotulo}>Data</span>
            <input type="date" value={pagoEm} onChange={(e) => setPagoEm(e.target.value)} className={campo} />
          </label>
          <label>
            <span className={rotulo}>Forma</span>
            <select value={forma} onChange={(e) => setForma(e.target.value as FormaDePagamento | "")} className={campo}>
              <option value="">Não informada</option>
              {FORMAS_DE_PAGAMENTO.map((f) => (
                <option key={f} value={f}>
                  {FORMA_DE_PAGAMENTO_LABELS[f]}
                </option>
              ))}
            </select>
          </label>
          {l.tipo === "ENTRADA" && <PreviaDaTaxa valorCents={l.valorCents} forma={forma || null} />}
          <div className="flex justify-end gap-2">
            <button type="button" className={botaoSecundario} onClick={() => setDialogo(null)}>
              Cancelar
            </button>
            <button type="button" className={botaoPrimario} disabled={!pagoEm || pagar.isPending} onClick={() => pagar.mutate({ id: l.id, pagoEm, formaDePagamento: forma || null })}>
              Confirmar
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogo === "estornar"} onOpenChange={(v) => !v && setDialogo(null)}>
        <DialogContent className={`max-w-sm ${flutuante}`}>
          <DialogHeader>
            <DialogTitle ajuda={AJUDA_FINANCEIRO.estornar}>Estornar lançamento</DialogTitle>
            <DialogDescription>
              O lançamento não é apagado: entra um oposto de {reais(l.valorCents)} ligado a ele, e os dois deixam de contar.
            </DialogDescription>
          </DialogHeader>
          <label>
            <span className={rotulo}>Motivo (opcional)</span>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex: lançado em duplicidade" className={campo} />
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className={botaoSecundario} onClick={() => setDialogo(null)}>
              Cancelar
            </button>
            <button type="button" className={botaoPerigo} disabled={estornar.isPending} onClick={() => estornar.mutate({ id: l.id, motivo: motivo || undefined })}>
              Estornar
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
